/**
 * MCR (Mahjong Competition Rules / Chinese Official) settings.
 *
 * Unlike HKOS — a family of house-rule variants with no single authoritative
 * source — MCR is a published, official ruleset (World Mahjong Organization,
 * 2006 rulebook). So this module aims to be a faithful implementation of
 * that document, not an open house-rule sandbox: the official values below
 * are the rulebook's own, not a house convention someone picked.
 *
 * Official values (2006 rulebook, ch. on scoring):
 *  - A hand needs at least 8 points (Basic Points, from the 81-pattern fan
 *    table) to legally declare a win — no "chicken hand" wins in MCR.
 *  - Extra Points: every non-winning player pays a flat 8 points to the
 *    winner, on top of Basic Points — this is separate from the minimum
 *    described above even though both happen to be 8 in the base rule.
 *  - Declaring a win below the minimum is a "False Hu" — the declarer
 *    forfeits a flat 10 points to each of the other three players instead
 *    of collecting anything.
 *
 * The Fun/Serious mode split below is NOT part of the official rulebook —
 * MCR's own settlement is inherently the "serious", zero-sum kind. It's
 * grafted on here to match the same choice HKOS offers players at Setup,
 * confirmed with Indra 2026-09-22. Serious mode is a straight implementation
 * of the rulebook (see `payout.ts`), but — like every other Serious-mode
 * setting in this file — its numbers are still editable house rules, same as
 * HKOS's own Serious mode; Fun mode reuses the same Basic/Extra point math
 * to compute how much the winner would have collected, then credits only
 * the winner with that amount rather than debiting the other three — same
 * "only the round winner ever gains, nobody else's total can go negative"
 * philosophy as HKOS's own Fun mode (CLAUDE.md rule 3). That philosophy is
 * also why Fun mode has no `falseWinPenalty` of its own: a below-minimum
 * declared win in Fun mode simply moves no points (like a draw) instead of
 * penalizing the declarer — penalizing anyone would break the "no negative
 * deltas, ever" invariant Fun mode exists to guarantee.
 *
 * A third mode, 'official', was added the same day (2026-09-22, later) once
 * Indra pointed out that Serious mode alone doesn't actually guarantee a
 * game is the real rulebook — its numbers are editable, so nothing stops
 * them drifting from official values. 'official' mode uses EXACTLY the same
 * settlement math as Serious mode (see `payout.ts`'s `effectiveSeriousSettings`)
 * but always reads the hardcoded rulebook defaults below, ignoring whatever
 * is actually stored in `settings.serious` — this is enforced structurally,
 * in the engine, not just by graying out a settings screen (which doesn't
 * exist yet anyway), so it can never silently drift even from a bug or a
 * bad migration. There is no `McrOfficialModeSettings` interface — there is
 * nothing to configure, that's the entire point of this mode.
 */

export type McrScoringMode = 'serious' | 'fun' | 'official';

/**
 * Settlement-mechanic settings that only mean something inside Serious
 * mode's zero-sum, multi-payer scheme. Nested here (rather than left at the
 * top level and ignored in Fun mode) so a Fun-mode settlement can't
 * accidentally read a concept — a penalty to forfeit, a discarder to charge
 * the full amount — that doesn't exist in a winner-only model. Same
 * reasoning as HKOS's own `SeriousModeSettings` nesting.
 */
export interface McrSeriousModeSettings {
  /** Basic Points (fan-table total) required to legally declare a win in this mode. Official value: 8. */
  minimumPoints: number;
  /** Flat points every non-winning player owes the winner, on top of Basic Points. Official value: 8. */
  extraPoints: number;
  /** Flat points forfeited to EACH other player when a declared win doesn't reach minimumPoints ("False Hu"). Official value: 10. */
  falseWinPenalty: number;
}

export interface McrFunModeSettings {
  /** Applied to the raw (Basic + Extra) total the winner would have collected in Serious mode's own formula. 1 = that total itself. */
  multiplier: number;
  /**
   * Basic Points required to legally declare a win in this mode. Defaults to
   * 0 so Fun mode games can allow a win with no scoring pattern behind it,
   * once patterns exist — independent of serious.minimumPoints, same
   * convention as HKOS's own per-mode minimumFan split.
   */
  minimumPoints: number;
  /** Flat points nominally added to Basic Points before the multiplier, mirroring extraPoints' role in Serious mode's formula. Independent of serious.extraPoints. */
  extraPoints: number;
}

export interface McrSettings {
  mode: McrScoringMode;
  serious: McrSeriousModeSettings;
  fun: McrFunModeSettings;
}

export const DEFAULT_SETTINGS: McrSettings = {
  mode: 'serious',
  serious: {
    minimumPoints: 8,
    extraPoints: 8,
    falseWinPenalty: 10,
  },
  fun: {
    multiplier: 1,
    minimumPoints: 0,
    extraPoints: 8,
  },
};

/**
 * The Serious-mode settlement values that actually apply right now.
 * 'official' mode ignores `settings.serious` entirely and always returns the
 * rulebook's own defaults — the structural guarantee described in this
 * file's header comment. 'fun' mode has no serious-shaped values at all;
 * callers that need them should have already branched on `mode === 'fun'`
 * before reaching here.
 */
export function effectiveSeriousSettings(settings: McrSettings): McrSeriousModeSettings {
  return settings.mode === 'official' ? DEFAULT_SETTINGS.serious : settings.serious;
}

/** The minimum points required for a legal win, given settings.mode. */
export function activeMinimumPoints(settings: McrSettings): number {
  return settings.mode === 'fun'
    ? settings.fun.minimumPoints
    : effectiveSeriousSettings(settings).minimumPoints;
}

/** Deep-merge a partial override onto the defaults. */
export function makeSettings(overrides: DeepPartial<McrSettings> = {}): McrSettings {
  return {
    ...DEFAULT_SETTINGS,
    ...overrides,
    serious: { ...DEFAULT_SETTINGS.serious, ...(overrides.serious ?? {}) },
    fun: { ...DEFAULT_SETTINGS.fun, ...(overrides.fun ?? {}) },
  } as McrSettings;
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? Partial<T[K]> : T[K];
};
